import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/image';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SliderInput = ({ label, value, min, max, onChange, id }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-xs uppercase tracking-widest text-gray-500">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(Math.min(max, Math.max(min, Number(e.target.value) || 0)))}
          className="w-16 bg-black border border-white/10 rounded-none px-2 py-1 text-white text-xs text-center focus:outline-none focus:border-white/30"
          data-testid={`${id}-input`}
        />
      </div>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-1 bg-white/10 appearance-none cursor-pointer accent-white"
      data-testid={`${id}-slider`}
    />
  </div>
);

export const SettingsTab = ({ settings, onRefresh }) => {
  const [socialLinks, setSocialLinks] = useState(settings.socialLinks);
  const [aboutText, setAboutText] = useState(settings.aboutText);
  const [heroAdj, setHeroAdj] = useState(settings.heroAdjustments || { opacity: 70, brightness: 0, contrast: 0 });
  const [savingLinks, setSavingLinks] = useState(false);
  const [savingAbout, setSavingAbout] = useState(false);
  const [savingAdj, setSavingAdj] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  React.useEffect(() => {
    setSocialLinks(settings.socialLinks);
    setAboutText(settings.aboutText);
    if (settings.heroAdjustments) setHeroAdj(settings.heroAdjustments);
  }, [settings]);

  const uploadFile = async (endpoint, file, setUploading) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/settings/${endpoint}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${endpoint === 'logo' ? 'Logo' : 'Hero image'} updated successfully`);
      onRefresh();
    } catch {
      toast.error(`Failed to update ${endpoint}`);
    } finally { setUploading(false); }
  };

  const handleSaveSocialLinks = async (e) => {
    e.preventDefault();
    setSavingLinks(true);
    try {
      await axios.put(`${API}/settings/social-links`, socialLinks);
      toast.success('Social links updated');
    } catch { toast.error('Failed to update social links'); }
    finally { setSavingLinks(false); }
  };

  const handleSaveAbout = async (e) => {
    e.preventDefault();
    setSavingAbout(true);
    try {
      await axios.put(`${API}/settings/about`, { about_text: aboutText });
      toast.success('About section updated');
    } catch { toast.error('Failed to update about section'); }
    finally { setSavingAbout(false); }
  };

  const handleSaveHeroAdj = async () => {
    setSavingAdj(true);
    try {
      await axios.put(`${API}/settings/hero-adjustments`, heroAdj);
      toast.success('Background adjustments saved');
      onRefresh();
    } catch { toast.error('Failed to save adjustments'); }
    finally { setSavingAdj(false); }
  };

  const inputClass = "w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors";
  const fileClass = `${inputClass} file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-sm file:font-semibold file:bg-white file:text-black hover:file:bg-gray-200`;
  const btnClass = "w-full bg-white text-black hover:bg-gray-200 rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50";

  const previewFilter = `brightness(${1 + heroAdj.brightness / 100}) contrast(${1 + heroAdj.contrast / 100})`;

  return (
    <div>
      <h2 className="text-2xl font-normal mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Site Settings</h2>

      <div className="border border-white/10 p-8 max-w-xl">
        <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Home Page Logo</h3>
        <p className="text-sm text-gray-400 mb-6">Upload a new logo image to replace the current one on the landing page.</p>
        {settings.logoUrl && (
          <div className="mb-6 border border-white/10 p-4 bg-neutral-900 flex items-center justify-center">
            <img src={resolveImageUrl(settings.logoUrl)} alt="Current Logo" className="max-h-32 object-contain" data-testid="current-logo-preview" />
          </div>
        )}
        <label className="block">
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) uploadFile('logo', f, setUploadingLogo); }} className={fileClass} disabled={uploadingLogo} data-testid="logo-upload-input" />
          {uploadingLogo && <p className="text-sm text-gray-400 mt-2">Uploading logo...</p>}
        </label>
      </div>

      <div className="border border-white/10 p-8 max-w-xl mt-10">
        <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Home Page Background</h3>
        <p className="text-sm text-gray-400 mb-6">Upload a new photo for the full-screen hero background on the landing page.</p>
        {settings.heroImage && (
          <div className="mb-6 border border-white/10 overflow-hidden relative">
            <img src={resolveImageUrl(settings.heroImage)} alt="Current Hero" className="w-full h-40 object-cover" style={{ opacity: heroAdj.opacity / 100, filter: previewFilter }} data-testid="current-hero-preview" />
          </div>
        )}
        <label className="block mb-8">
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) uploadFile('hero', f, setUploadingHero); }} className={fileClass} disabled={uploadingHero} data-testid="hero-upload-input" />
          {uploadingHero && <p className="text-sm text-gray-400 mt-2">Uploading hero image...</p>}
        </label>

        <div className="space-y-5 border-t border-white/10 pt-6">
          <h4 className="text-sm uppercase tracking-widest text-gray-400 mb-4">Background Adjustments</h4>
          <SliderInput label="Transparency" value={heroAdj.opacity} min={0} max={100} onChange={(v) => setHeroAdj({ ...heroAdj, opacity: v })} id="hero-opacity" />
          <SliderInput label="Brightness" value={heroAdj.brightness} min={-50} max={50} onChange={(v) => setHeroAdj({ ...heroAdj, brightness: v })} id="hero-brightness" />
          <SliderInput label="Contrast" value={heroAdj.contrast} min={-50} max={50} onChange={(v) => setHeroAdj({ ...heroAdj, contrast: v })} id="hero-contrast" />
          <button onClick={handleSaveHeroAdj} disabled={savingAdj} className={btnClass} data-testid="save-hero-adj-btn">
            {savingAdj ? 'Saving...' : 'Save Adjustments'}
          </button>
        </div>
      </div>

      <div className="border border-white/10 p-8 max-w-xl mt-10">
        <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Social Media Links</h3>
        <p className="text-sm text-gray-400 mb-6">Update the social media links shown in the footer across all pages.</p>
        <form onSubmit={handleSaveSocialLinks} className="space-y-5" data-testid="social-links-form">
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Instagram URL</label>
            <input type="text" value={socialLinks.instagram} onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })} placeholder="https://instagram.com/..." className={inputClass} data-testid="social-instagram-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">TikTok URL</label>
            <input type="text" value={socialLinks.tiktok} onChange={(e) => setSocialLinks({ ...socialLinks, tiktok: e.target.value })} placeholder="https://tiktok.com/..." className={inputClass} data-testid="social-tiktok-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Email Address</label>
            <input type="email" value={socialLinks.email} onChange={(e) => setSocialLinks({ ...socialLinks, email: e.target.value })} placeholder="email@example.com" className={inputClass} data-testid="social-email-input" />
          </div>
          <button type="submit" disabled={savingLinks} className={btnClass} data-testid="save-social-links-btn">{savingLinks ? 'Saving...' : 'Save Social Links'}</button>
        </form>
      </div>

      <div className="border border-white/10 p-8 max-w-xl mt-10">
        <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>About Section</h3>
        <p className="text-sm text-gray-400 mb-6">Edit the about text shown on the home page. Leave empty to hide the section.</p>
        <form onSubmit={handleSaveAbout} className="space-y-5" data-testid="about-form">
          <textarea value={aboutText} onChange={(e) => setAboutText(e.target.value)} rows={6} placeholder="Write about yourself, your photography style, experience..." className={`${inputClass} resize-none`} data-testid="about-text-input" />
          <button type="submit" disabled={savingAbout} className={btnClass} data-testid="save-about-btn">{savingAbout ? 'Saving...' : 'Save About Section'}</button>
        </form>
      </div>
    </div>
  );
};

export default SettingsTab;
